import { useState } from 'react'
import { router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { BackofficeOfficerAssignment, BackofficeStaffOption } from '@/types'
import { Users, UserPlus, RefreshCw, Trash2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type Props = {
  business: { id: string; name: string }
  compliance_assignment: BackofficeOfficerAssignment | null
  support_assignment: BackofficeOfficerAssignment | null
  compliance_staff: BackofficeStaffOption[]
  support_staff: BackofficeStaffOption[]
}

function AssignmentSlot({
  title,
  assignment,
  staff,
  businessId,
  type,
}: {
  title: string
  assignment: BackofficeOfficerAssignment | null
  staff: BackofficeStaffOption[]
  businessId: string
  type: 'compliance' | 'support'
}) {
  const confirm = useConfirm()
  const [selectedId, setSelectedId] = useState<string>('')
  const [reassignId, setReassignId] = useState<string>('')
  const basePath = `/backoffice/businesses/${businessId}/officer_assignments`

  function handleAssign() {
    if (!selectedId) return
    router.post(basePath, { identity_id: selectedId, type }, { preserveScroll: true })
  }

  function handleReassign() {
    if (!reassignId || !assignment) return
    router.patch(`${basePath}/${assignment.id}`, { identity_id: reassignId }, { preserveScroll: true })
  }

  async function handleRemove() {
    if (!assignment) return
    const ok = await confirm({
      title: `Remove ${title}`,
      description: `Remove the current ${title.toLowerCase()} assignment?`,
      confirmLabel: 'Remove',
      destructive: true,
    })
    if (ok) {
      router.delete(`${basePath}/${assignment.id}`, { preserveScroll: true })
    }
  }

  return (
    <div className="border rounded-lg">
      <div className="flex items-center gap-2 px-4 py-3 border-b">
        <Users className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-sm font-medium text-foreground">{title}</h2>
      </div>

      {assignment ? (
        <div className="px-4 py-4 space-y-3">
          <div>
            <div className="text-sm font-medium text-foreground">{assignment.email}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {assignment.assigned_by_email
                ? `Assigned by ${assignment.assigned_by_email}`
                : 'Auto-assigned by system'}
              {' · '}
              {new Date(assignment.created_at).toLocaleDateString()}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Select value={reassignId} onValueChange={setReassignId}>
              <SelectTrigger className="flex-1 h-8 text-xs">
                <SelectValue placeholder="Reassign to..." />
              </SelectTrigger>
              <SelectContent>
                {staff
                  .filter(s => s.id !== assignment.identity_id)
                  .map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.email_address}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleReassign} disabled={!reassignId}>
              <RefreshCw className="w-3 h-3 mr-1" />
              Reassign
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs text-destructive hover:text-destructive" onClick={handleRemove}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-4 py-4 space-y-3">
          <p className="text-sm text-muted-foreground">Not assigned</p>
          <div className="flex items-center gap-2">
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="flex-1 h-8 text-xs">
                <SelectValue placeholder="Select staff member..." />
              </SelectTrigger>
              <SelectContent>
                {staff.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.email_address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleAssign} disabled={!selectedId}>
              <UserPlus className="w-3 h-3 mr-1" />
              Assign
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function OfficerAssignmentsIndex({
  business,
  compliance_assignment,
  support_assignment,
  compliance_staff,
  support_staff,
}: Props) {
  return (
    <BackofficeLayout
      title="Officer Assignments"
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Businesses', href: '/backoffice/businesses' },
        { label: business.name, href: `/backoffice/businesses/${business.id}` },
        { label: 'Officer Assignments' },
      ]}
    >
      <div className="space-y-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight mb-1">Officer Assignments</h1>
          <p className="text-sm text-muted-foreground">
            Manage compliance and support officer assignments for {business.name}.
          </p>
        </div>

        <AssignmentSlot
          title="Compliance Officer"
          assignment={compliance_assignment}
          staff={compliance_staff}
          businessId={business.id}
          type="compliance"
        />

        <AssignmentSlot
          title="Support Officer"
          assignment={support_assignment}
          staff={support_staff}
          businessId={business.id}
          type="support"
        />
      </div>
    </BackofficeLayout>
  )
}
